import { type GetStaticPaths, type GetStaticProps } from 'next';
import React from 'react';

import { BootcampStatus } from '../../__generated-gql-types__/globalTypes';
import BootcampPageComponent, {
  type BootcampPageProps,
} from '../../components/bootcamp/BootcampPage';
import LexGuardedPage from '../../components/page/LexGuardedPage';
import Page from '../../components/page/Page';
import PageContent, {
  getStaticPageContentQuery,
  type PageContentDataProps,
} from '../../components/page/PageContent';
import { LexSlug } from '../../context/LexContext';
import { initializeApollo } from '../../lib/apolloClient';
import { BASE_SEARCH_PATH } from '../../utils/search';

import {
  BootcampPageBootcampBySlugDocument,
  type BootcampPageBootcampBySlugQuery,
  type BootcampPageBootcampBySlugQueryVariables,
  BootcampPageCategoryBySlugDocument,
  type BootcampPageCategoryBySlugQuery,
  type BootcampPageCategoryBySlugQueryVariables,
  BootcampPageCategoryEventsCountDocument,
  type BootcampPageCategoryEventsCountQuery,
  type BootcampPageCategoryEventsCountQueryVariables,
  BootcampPageSearchBootcampsDocument,
  type BootcampPageSearchBootcampsQuery,
  type BootcampPageSearchBootcampsQueryVariables,
} from './__generated-gql-types__/[bootcampSlug].generated';

type BootcampsPageProps = BootcampPageProps & {
  pageContentProps: PageContentDataProps;
};

const BootcampsPage: React.FC<BootcampsPageProps> = (props) => {
  return (
    <LexGuardedPage lex={LexSlug.BOOTCAMP_REVAMP}>
      <Page title={props.bootcamp.title}>
        <PageContent initialData={props.pageContentProps}>
          <BootcampPageComponent {...props} />
        </PageContent>
      </Page>
    </LexGuardedPage>
  );
};

export interface QueryType extends Record<string, string | undefined> {
  bootcampSlug: string;
}

export const getStaticPaths: GetStaticPaths<QueryType> = async () => {
  const paths: Array<{ params: QueryType }> = [];
  const apolloClient = initializeApollo();
  const { data: categoryCounts } = await apolloClient.query<
    BootcampPageCategoryEventsCountQuery,
    BootcampPageCategoryEventsCountQueryVariables
  >({ query: BootcampPageCategoryEventsCountDocument });
  const popularCategories = categoryCounts?.categoryEventsCount.map(
    (category) => category.category,
  );
  const { data: bootcampsData } = await apolloClient.query<
    BootcampPageSearchBootcampsQuery,
    BootcampPageSearchBootcampsQueryVariables
  >({
    query: BootcampPageSearchBootcampsDocument,
    variables: {
      categoriesFilter: popularCategories.map((category) => category.id),
    },
  });

  if (!bootcampsData) {
    return { paths: [], fallback: false };
  }

  bootcampsData.searchBootcamps.results.forEach((bootcamp) => {
    paths.push({ params: { bootcampSlug: bootcamp.slug } });
  });
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<
  BootcampPageProps,
  QueryType
> = async ({ params }) => {
  const { bootcampSlug } = params ?? {};
  if (!bootcampSlug) {
    return {
      redirect: { destination: BASE_SEARCH_PATH, permanent: false },
    };
  }
  const apolloClient = initializeApollo();
  const pageContentData = await getStaticPageContentQuery();
  const { data: bootcampsData, errors } = await apolloClient.query<
    BootcampPageBootcampBySlugQuery,
    BootcampPageBootcampBySlugQueryVariables
  >({
    query: BootcampPageBootcampBySlugDocument,
    variables: {
      slug: bootcampSlug,
    },
  });

  if (
    errors?.length ||
    bootcampsData?.bootcampBySlug.status !== BootcampStatus.LIVE
  ) {
    return { notFound: true };
  }

  const categorySlug = bootcampsData.bootcampBySlug?.category?.slug;

  const { data: categoryData } = await apolloClient.query<
    BootcampPageCategoryBySlugQuery,
    BootcampPageCategoryBySlugQueryVariables
  >({
    query: BootcampPageCategoryBySlugDocument,
    variables: { slug: categorySlug ?? '' },
    context: { skip: !categorySlug },
  });
  if (categorySlug && !categoryData?.categoryBySlug) {
    return {
      redirect: { destination: BASE_SEARCH_PATH, permanent: false },
    };
  }
  return {
    props: {
      bootcamp: bootcampsData.bootcampBySlug,
      totalBootcamps: bootcampsData.bootcampBySlug,
      pageContentProps: pageContentData,
      category: categoryData.categoryBySlug,
    },
    revalidate: 3600,
  };
};

export default BootcampsPage;
